import type { Corners, Point } from './types.js';

// =============================================================================
// Global ↔ local viewport coord conversion (CALIBRATION_UX §6.6).
// The destination coord space is global [0,1]×[0,1] across the whole Output
// display. Admin wall previews render a sub-viewport (bbox + overscan) and need
// to map back and forth between global coords and the local preview box.
// =============================================================================

export type BBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/** Axis-aligned bounding box of a set of points. Empty input → unit box. */
export function computeBBox(corners: readonly Point[]): BBox {
  if (corners.length === 0) {
    return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of corners) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** BBox expanded uniformly by `overscan` fraction (default 10%). */
export function computeViewportWithOverscan(
  corners: readonly Point[],
  overscan = 0.1,
): BBox {
  const bbox = computeBBox(corners);
  const w = Math.max(1e-6, bbox.maxX - bbox.minX);
  const h = Math.max(1e-6, bbox.maxY - bbox.minY);
  return {
    minX: bbox.minX - w * overscan,
    minY: bbox.minY - h * overscan,
    maxX: bbox.maxX + w * overscan,
    maxY: bbox.maxY + h * overscan,
  };
}

/** Map a global coord into the viewport's local [0,1] space. */
export function globalToLocal(g: Point, viewport: BBox): Point {
  const vw = viewport.maxX - viewport.minX;
  const vh = viewport.maxY - viewport.minY;
  return {
    x: vw === 0 ? 0 : (g.x - viewport.minX) / vw,
    y: vh === 0 ? 0 : (g.y - viewport.minY) / vh,
  };
}

/** Map a local [0,1] coord back to global. */
export function localToGlobal(l: Point, viewport: BBox): Point {
  return {
    x: viewport.minX + l.x * (viewport.maxX - viewport.minX),
    y: viewport.minY + l.y * (viewport.maxY - viewport.minY),
  };
}

/** Whether a global point lies within a viewport (inclusive). */
export function isInViewport(g: Point, viewport: BBox): boolean {
  return (
    g.x >= viewport.minX &&
    g.x <= viewport.maxX &&
    g.y >= viewport.minY &&
    g.y <= viewport.maxY
  );
}

/**
 * Compass direction from viewport center toward a point outside the viewport.
 * Returns null if the point is inside. Used for edge arrow indicators when a
 * handle is dragged beyond the preview bounds (CALIBRATION_UX §6.6).
 */
export type OutOfViewportDirection =
  | 'n'
  | 'ne'
  | 'e'
  | 'se'
  | 's'
  | 'sw'
  | 'w'
  | 'nw';

/**
 * Whether a quad (4 vertices in order TL, TR, BR, BL) is convex and wound
 * consistently. A self-intersecting or degenerate quad produces a singular
 * homography (HANDOFF §10.2). Cross products of consecutive edges must all
 * share the same non-zero sign.
 */
export function isValidQuad(corners: Corners): boolean {
  let sign: number | null = null;
  for (let i = 0; i < 4; i++) {
    const a = corners[i] as Point;
    const b = corners[(i + 1) % 4] as Point;
    const c = corners[(i + 2) % 4] as Point;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-10) return false; // collinear
    const s = cross > 0 ? 1 : -1;
    if (sign === null) sign = s;
    else if (sign !== s) return false; // orientation flip → self-intersect
  }
  return true;
}

export function directionOutOfViewport(
  g: Point,
  viewport: BBox,
): OutOfViewportDirection | null {
  if (isInViewport(g, viewport)) return null;
  const north = g.y < viewport.minY;
  const south = g.y > viewport.maxY;
  const west = g.x < viewport.minX;
  const east = g.x > viewport.maxX;
  if (north && east) return 'ne';
  if (north && west) return 'nw';
  if (south && east) return 'se';
  if (south && west) return 'sw';
  if (north) return 'n';
  if (south) return 's';
  if (east) return 'e';
  return 'w';
}
